import React, { useEffect, useRef, useImperativeHandle, useState } from "react";
import PropTypes from "prop-types";
import { scrollToErrorElement } from "../../utils/dom";
import classNames from "classnames";
import "../dropdown/style.scss";
import { IS_MOBILE } from "../../constants";

export default function Dropdown(props) {
  const { options = [], onChange, onFocus, onBlur, tel = false ,oneOfEdit = false, maxlength = "",showError, className, nodata, warningMessage, selectCode, optionsMaxHeightVal, preventScroll = false } = props;

  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [currentOptionIndex, setCurrentOptionIndex] = useState(-1);
  const [selectedOption, setSelectedOption] = useState(props.selectedOption);
  const [currentOptions, setCurrentOptions] = useState(options);
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [enableFilter, setEnableFilter] = useState(props.enableFilter || false);

  const notchWidth = { width: "auto" };
  const optionsMaxHeight = optionsMaxHeightVal ? optionsMaxHeightVal : 600;

  const wrapperRef = useRef(null);
  const ulRef = useRef(null);
  const labelRef = useRef(null);
  const input = useRef(null);

  useImperativeHandle(props.cRef, () => ({
    option: selectedOption, 
  }));

  const labelClasses = classNames("floating-label", {
    "floating-above": isOpen || selectedOption || isFocused
  });

  const outlineLeadingClasses = classNames("notched-outline__leading", {
    focus: isOpen || isFocused,
    "show-warning": showError
  });

  const outlineNotchClasses = classNames("notched-outline__notch", {
    focus: isOpen || isFocused,
    "has-content": selectedOption,
    "show-warning": showError
  });
  const outlineTrailingClasses = classNames("notched-outline__trailing", {
    focus: isOpen || isFocused,
    "show-warning": showError
  });

  const wrapperClassList = classNames("dropdown", className);

  const contentClassList = classNames("dropdown-content", {
    open: isOpen
  });
  const arrowIconClassList = classNames("arrow", {
    open: isOpen
  });

  const inputClasses = classNames("filter-input");

  const selectClasses = classNames("vertical-align", { error: showError }, { active: tel });

  useEffect(() => {
    if (selectCode && options.length) {
      let sltItem = options.filter(item => item.code.toLowerCase() === selectCode.toLowerCase());
      if (sltItem.length > 0) {
        setSelectedOption(sltItem[0]);
        setSelectedIndex(options.indexOf(sltItem[0]));
      }
    }
    if (currentOptions.length === 0 && options.length === 0) {
      return;
    }
    setCurrentOptions(options);
  }, [options]);

  useEffect(() => {
    setSelectedOption(props.selectedOption);
  }, [props.selectedOption]);

  useEffect(() => {
    !preventScroll && scrollToErrorElement(wrapperRef.current);
  }, [showError]);

  const onComponentBlur = event => {
    event.preventDefault();
    setIsFocused(false);
    setIsOpen(false);
    onBlur(event);
  };

  const optionSelected = (evt, selectedOption, index) => {
    if(selectedOption.code === 'editSelect' && oneOfEdit) {//单独修改某一个
      setEnableFilter(true)
      setIsFocused(true);
      setTimeout(v=>{
        input.current.focus();
        filterOptions('')
      })
    }
    if(selectedOption.code !== 'editSelect' && oneOfEdit) {
      setEnableFilter(false)
      setIsFocused(false);
    }
    if ((!selectedOption?.disabled && isOpen) || (IS_MOBILE && !selectedOption?.disabled)) {
      setSelectedOption(selectedOption);
      setSelectedIndex(index);
      onChange(selectedOption);
      setIsOpen(false);
    }
  };

  const activeOption = index => {
    return classNames("list-item", {
      selected: selectedIndex === index,
      current: currentOptionIndex === index && selectedIndex !== currentOptionIndex
    });
  };

  const highlightListElement = optionElement => {
    const index = options.indexOf(optionElement);
    const optionsHeight = 48;
    ulRef.current.scroll(0, optionsHeight * index);
    setCurrentOptionIndex(index);
  };

  const filterOptions = text => {
    if (text === "") {
      setSelectedOption(null);
      onChange("");
    } else {
      const firstOptionWithMatch = options.filter(({ value }) => value.toLowerCase().startsWith(text.toLowerCase()))[0];
      setSelectedOption({ value: text });
      highlightListElement(firstOptionWithMatch);
    }
  };

  const handleKeyPressSelect = event => {
    event.preventDefault();
    if (!isOpen) {
      setIsOpen(true);
    } else {
      if ( oneOfEdit && currentOptions[currentOptionIndex] ){
        if ( currentOptions[currentOptionIndex].code === 'editSelect' && oneOfEdit ) {      
          setEnableFilter(true)
          setIsFocused(true);
          setTimeout(v=>{
            input.current.focus();
            filterOptions('')
          })
        }
        if( currentOptions[currentOptionIndex].code !== 'editSelect' && oneOfEdit ){
          setEnableFilter(false)
          setIsFocused(false);
        }
      }
      setIsOpen(false);
      setSelectedIndex(currentOptionIndex);
      setSelectedOption(currentOptions[currentOptionIndex]);
      onChange(currentOptions[currentOptionIndex]);
    }
  };

  const handleArrowDown = event => {
    event.preventDefault();
    const nextOptionIndex = currentOptionIndex + 1 <= currentOptions.length ? currentOptionIndex + 1 : 0;
    setCurrentOptionIndex(nextOptionIndex);
  };

  const handleArrowUp = event => {
    event.preventDefault();
    const prevOptionIndex = currentOptionIndex >= 0 ? currentOptionIndex - 1 : currentOptions.length - 1;
    setCurrentOptionIndex(prevOptionIndex);
  };

  const goToLetter = letter => {
    // find first option that stats with leter
    const firstOptionWithMatch = options.filter(({ value }) => value.toLowerCase().startsWith(letter))[0];
    highlightListElement(firstOptionWithMatch);
  };

  const handleArrowNavigation = event => {
    const ARROW_DOWN = 40;
    const ARROW_UP = 38;
    const ENTER = 13;
    const SPACE = 32;
    const A = 65;
    const Z = 90;

    const alphabet = ["a", "b", "c", "d", "e", "f", "g", "h", "i", "j", "k", "l", "m", "n", "o", "p", "q", "r", "s", "t", "u", "v", "w", "x", "y", "z"];

    switch (event.keyCode) {
      case ARROW_DOWN:
        handleArrowDown(event);
        break;
      case ARROW_UP:
        handleArrowUp(event);
        break;
      case ENTER:
        handleKeyPressSelect(event);
        break;
      case SPACE:
        handleKeyPressSelect(event);
        break;
      default:
        if (event.keyCode >= A && event.keyCode <= Z && !enableFilter) {
          goToLetter(alphabet[event.keyCode - A]);
        }
    }
  };

  return (
    <div
      className={wrapperClassList}
      ref={wrapperRef}
      onClick={() => {
        setIsOpen(!isOpen);
      }}
      onFocus={() => {
        setIsFocused(true);
        //setting focus on input so user can type right away
        if (enableFilter && !IS_MOBILE) {
          input.current.focus();
        }
        onFocus();
      }}
      onKeyDown={event => handleArrowNavigation(event)}
      onBlur={onComponentBlur}
      tabIndex={0}
    >
      <div className="select__externalLink">
        <span className={arrowIconClassList}>
          <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24">
            <path d="M0 0h24v24H0V0z" fill="none" />
            <path d="M7 10l5 5 5-5H7z" />
          </svg>
        </span>
        {enableFilter && (
          <input
            ref={input}
            className={inputClasses}
            value={selectedOption ? selectedOption.value : ""}
            maxlength={maxlength}
            onChange={event => {
              filterOptions(event.currentTarget.value);
            }}
          />
        )}
        {!enableFilter && (
          <div className={inputClasses} value={selectedOption ? (selectedOption.value)?.replace(props.reg,'') : ""}>
            <span className={selectClasses}>{selectedOption ?(selectedOption.value)?.replace(props.reg,'') : ""}</span>
          </div>
        )}
        <div className={"notched-outline"}>
          <div className={outlineLeadingClasses}></div>
          <div className={outlineNotchClasses} style={notchWidth}>
            {props.placeholder && props.placeholder !== "" && (
              <label ref={labelRef} className={labelClasses}>
                {props.placeholder}
              </label>
            )}
          </div>
          <div className={outlineTrailingClasses}></div>
        </div>
      </div>
      <ul className={contentClassList} style={{ maxHeight: `${optionsMaxHeight}px` }} ref={ulRef}>
        {currentOptions != null &&
          currentOptions.length > 0 &&
          currentOptions.map((option, index) => {
            const optionClass = classNames(activeOption(index), {
              disabled: option.disabled
            });
            return (
              <li className={optionClass} onMouseDown={evt => optionSelected(evt, option, index)} key={index} onMouseOver={() => setCurrentOptionIndex(index)}>
                <div className="value">
                  <span>{option.value}</span>
                  {option.values}
                </div>
                <div className="right">{option?.otherValue}</div>
              </li>
            );
          })}
          {currentOptions != null && currentOptions.length === 0 && nodata &&
           <li className="list-item nodata">{nodata}</li>
          }
      </ul>
      {showError && <span className="warning-message">{warningMessage}</span>}
    </div>
  );
}

Dropdown.propTypes = {
  className: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      code: PropTypes.string.isRequired
    })
  ),
  selectedOption: PropTypes.shape({
    value: PropTypes.string.isRequired,
    code: PropTypes.string.isRequired
  }),
  placeholder: PropTypes.string,
  warningMessage: PropTypes.string,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func
};

Dropdown.defaultProps = {
  className: "",
  onChange: () => {},
  onFocus: () => {},
  onBlur: () => {}
};
